import cx from 'classnames';
import React, { forwardRef, ReactNode, Ref } from 'react';
import { Link } from 'react-router-dom';
import Color, {
  BackgroundColors,
  BorderColors,
  HoverColors,
  TextColors,
} from '../../utilities/Color.js';
import { IconType } from '../Icon/Icon.types.js';
import Icon from '../Icon/index.js';
import Loading from '../Loading/index.js';
import Tooltip from '../Tooltip/index.js';

export interface ButtonProps {
  children?: ReactNode;
  id?: string;
  disabled?: boolean;
  hidden?: boolean;
  external?: boolean;
  loading?: boolean;
  label?: string;
  link?: string;
  target?: string;
  onClick?: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  type?: 'button' | 'submit';
  variant?: 'default' | 'outline' | 'text' | 'tertiary';
  shape?: 'default' | 'circle';
  textColor?: Color;
  icon?: IconType;
  iconPosition?: 'start' | 'end';
  shrink?: boolean;
  download?: string | false;
  className?: string;
  tooltip?: string;
  wrap?: boolean;
}

// NOTE: Storybook doesn't support autogenerating controls for default exports of forwardRef components
export const Button = forwardRef(function InnerButton(
  {
    children,
    disabled = false,
    external = false,
    label,
    link,
    onClick = (): void => undefined,
    type = 'button',
    variant = 'default',
    shape = 'default',
    textColor = Color.Text,
    loading = false,
    icon,
    iconPosition = 'start',
    shrink = false,
    download = false,
    className = '',
    tooltip = '',
    wrap = false,
    ...rest
  }: ButtonProps,
  ref: Ref<HTMLButtonElement>,
): JSX.Element {
  const classes = {
    'leading-none font-semibold uppercase flex gap-x-2 items-center justify-center tracking-button': true,
    'whitespace-nowrap': !wrap,
    '!bg-opacity-38 cursor-not-allowed': disabled || loading,
    [TextColors[Color.TextSecondary]]:
      (disabled || loading) && variant === 'text' && textColor === Color.Text,
    'opacity-[.39]': (disabled || loading) && variant === 'text' && textColor !== Color.Text,
    [`border ${BorderColors[Color.Text]}`]: variant === 'outline',
    'inline-flex': variant === 'text',
    'w-field max-w-full':
      !shrink && shape !== 'circle' && (variant === 'default' || variant === 'outline'),
    'py-4 px-6': shape !== 'circle' && (variant === 'default' || variant === 'outline'),
    'p-0 h-[3.125rem] w-[3.125rem]': shape === 'circle',
    'rounded-sm': variant !== 'text' && shape !== 'circle',
    'rounded-full': shape === 'circle',
    'py-2 px-3': variant === 'tertiary',
    [`${BackgroundColors[Color.Brand]} ${TextColors[Color.White]} ${
      HoverColors.Background[Color.PrimaryHover]
    }`]: variant === 'default' || variant === 'tertiary',
    [`${TextColors[textColor]}`]: variant !== 'default' && !!textColor,
    '!bg-transparent': variant !== 'default' && variant !== 'tertiary',
  };

  const content = loading ? (
    <span className="flex relative">
      <span className="absolute top-0 right-1/2">
        <Loading color="inherit" size="MD" />
      </span>
      <span className="invisible">{children}</span>
    </span>
  ) : (
    <>
      {icon && iconPosition === 'start' && <Icon type={icon} size="MD" />}
      <span>{children}</span>
      {icon && iconPosition === 'end' && <Icon type={icon} size="MD" />}
    </>
  );

  if (external && link && !disabled) {
    return (
      // biome-ignore lint/a11y/useValidAnchor: required by design
      <a
        className={cx(
          classes,
          { 'inline-block text-center': !variant.includes('text') },
          className,
        )}
        download={download}
        href={link}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        data-cy={rest.id}
        {...rest}
      >
        {content}
      </a>
    );
  }

  if (link && !disabled) {
    return (
      <Link
        download={download}
        className={cx(
          classes,
          { 'inline-block text-center': !variant.includes('text') },
          className,
        )}
        to={link}
        onClick={onClick}
        data-cy={rest.id}
        {...rest}
      >
        {content}
      </Link>
    );
  }

  const buttonMarkup = (
    <button
      type={type}
      aria-label={label}
      className={cx(classes, className)}
      onClick={onClick}
      disabled={disabled || loading}
      ref={ref}
      data-cy={rest.id}
      {...rest}
    >
      {content}
    </button>
  );

  return tooltip ? <Tooltip title={tooltip}>{buttonMarkup}</Tooltip> : buttonMarkup;
});
export default Button;
